import {
  createFileRoute,
  Link,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useAction, useConvexAuth, useQuery } from "convex/react";
import { useCallback, useEffect, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/macro";
import { useAuthActions } from "@convex-dev/auth/react";
import { ChevronLeft } from "lucide-react";
import { api } from "../../convex/_generated/api";

import Header from "../components/Header";
import DesktopHeaderTabs from "../components/admin/DesktopHeaderTabs";
import { useLocalizedPrice } from "../hooks/useLocalizedPrice";
import { clearBuilderFlowState } from "../lib/builderFlowStorage";
import { getPublicSiteHost } from "../utils/publicSiteHost";

export const Route = createFileRoute("/account")({
  component: AccountPage,
});

function AccountPage() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const { signOut } = useAuthActions();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({
        search: { entry: undefined, flow: undefined, template: undefined },
        to: "/auth/signin",
      });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const accountInfo = useQuery(
    api.subscriptions.getAccountInfo,
    isAuthenticated ? {} : "skip",
  );
  const mySites = useQuery(
    api.weddingSites.getMySites,
    isAuthenticated ? {} : "skip",
  );
  const rsvpsResult = useQuery(
    api.rsvp.listForSite,
    mySites?.[0]?._id ? { weddingSiteId: mySites[0]._id } : "skip",
  );
  const createPortalSession = useAction(api.stripeActions.createPortalSession);
  const createCheckoutSession = useAction(
    api.stripeActions.createCheckoutSession,
  );
  const localizedPrice = useLocalizedPrice();
  const [isSubscribing, setIsSubscribing] = useState(false);

  const site = mySites?.[0];
  const isSubscribed = accountInfo?.subscriptionStatus === "active";
  const totalAttending =
    rsvpsResult?.rsvps.filter((r: { attending: boolean }) => r.attending)
      .length ?? 0;

  const handleManageSubscription = useCallback(async () => {
    try {
      const returnUrl = window.location.href;
      const { url } = await createPortalSession({ returnUrl });
      window.location.href = url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to create portal session:", error);
    }
  }, [createPortalSession]);

  const handleSubscribe = useCallback(async () => {
    if (isSubscribing) {
      return;
    }
    setIsSubscribing(true);
    try {
      const returnUrl = window.location.href;
      const { url } = await createCheckoutSession({
        country: localizedPrice?.country ?? undefined,
        returnUrl,
      });
      window.location.href = url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to create checkout session:", error);
      setIsSubscribing(false);
    }
  }, [createCheckoutSession, isSubscribing, localizedPrice]);

  const handleSignOut = useCallback(async () => {
    clearBuilderFlowState();
    await signOut();
  }, [signOut]);

  const handleBack = useCallback(() => {
    if (canGoBack) {
      router.history.back();
      return;
    }
    if (site) {
      void navigate({ params: { slug: site.slug }, to: "/admin/$slug" });
      return;
    }
    void navigate({ to: "/" });
  }, [canGoBack, navigate, router, site]);

  if (authLoading || (isAuthenticated && accountInfo === undefined)) {
    return (
      <div className="min-h-screen bg-[var(--color-page)]">
        <Header />
        <main className="flex items-center justify-center px-4 py-16">
          <div className="text-sm text-[var(--color-muted)]">
            <Trans>Cargando...</Trans>
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--color-page)]">
      <Header
        navContent={
          site ? (
            <DesktopHeaderTabs
              activeTab={null}
              editHref={`/admin/${site.slug}?tab=edit`}
              rsvpCount={totalAttending}
              rsvpHref={`/admin/${site.slug}?tab=rsvp`}
            />
          ) : undefined
        }
      />
      <main className="max-w-lg mx-auto px-4 py-10">
        <button
          aria-label={t`Volver`}
          className="mb-6 inline-flex cursor-pointer items-center gap-1 text-sm text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
          data-testid="account-back-button"
          onClick={handleBack}
          type="button"
        >
          <ChevronLeft
            aria-hidden="true"
            className="h-4 w-4"
            strokeWidth={1.8}
          />
          <Trans>Volver</Trans>
        </button>
        <h1 className="mb-8 font-display text-5xl italic leading-none text-[var(--color-ink)]">
          <Trans>Cuenta</Trans>
        </h1>

        {/* Account info */}
        <section className="app-card mb-4 p-5">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.24em] text-[var(--color-muted)]">
            <Trans>Cuenta</Trans>
          </h2>
          <div
            className="text-sm text-[var(--color-ink)]"
            data-testid="account-email"
          >
            {accountInfo?.email ?? "—"}
          </div>
        </section>

        {/* Site link */}
        {site && (
          <section className="app-card mb-4 p-5">
            <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.24em] text-[var(--color-muted)]">
              <Trans>Mi Invitación</Trans>
            </h2>
            <div className="flex items-center justify-between">
              <Link
                className="app-link text-sm"
                params={{ slug: site.slug }}
                to="/love/$slug"
              >
                {getPublicSiteHost()}/love/{site.slug}
              </Link>
              <Link
                className="cursor-pointer text-xs text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
                params={{ slug: site.slug }}
                to="/admin/$slug"
              >
                <Trans>Editar</Trans> →
              </Link>
            </div>
          </section>
        )}

        {/* Subscription */}
        <section
          className="app-card mb-4 p-5"
          data-testid="subscription-section"
        >
          <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.24em] text-[var(--color-muted)]">
            <Trans>Suscripción</Trans>
          </h2>

          {isSubscribed ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--color-accent)]">
                  <Trans>Plan Premium</Trans>
                  <span className="text-green-600">✓</span>
                </span>
              </div>
              <button
                className="app-link cursor-pointer text-xs"
                data-testid="manage-subscription-btn"
                onClick={handleManageSubscription}
                type="button"
              >
                <Trans>Gestionar suscripción</Trans>
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-[var(--color-ink)]">
                  <Trans>Plan gratuito</Trans>
                </span>
              </div>
              <div className="mb-4 text-xs text-[var(--color-muted)]">
                <Trans>
                  {accountInfo?.aiGenerationsUsed ?? 0}/3 generaciones de IA
                  usadas
                </Trans>
              </div>
              <button
                className="app-link inline-flex items-center gap-2 cursor-pointer text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="subscribe-btn"
                disabled={isSubscribing}
                onClick={handleSubscribe}
                type="button"
              >
                {localizedPrice ? (
                  <Trans>Suscribirse · {localizedPrice.formatted}/mes</Trans>
                ) : (
                  <Trans>Suscribirse</Trans>
                )}
                {isSubscribing && (
                  <span
                    aria-hidden="true"
                    className="h-3.5 w-3.5 rounded-full border-2 border-current/30 border-t-current animate-spin"
                  />
                )}
              </button>
            </div>
          )}
        </section>

        {/* Support */}
        <section className="app-card mb-4 p-5">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.24em] text-[var(--color-muted)]">
            <Trans>Soporte</Trans>
          </h2>
          <p className="mb-2 text-sm text-[var(--color-muted)]">
            <Trans>
              ¿Necesitas ayuda? Escríbenos a{" "}
              <a className="app-link" href="mailto:soporte@bodaweb.site">
                soporte@bodaweb.site
              </a>
            </Trans>
          </p>
          <p className="text-sm text-[var(--color-muted)]">
            <Trans>
              Si quieres un diseño completamente personalizado para tu boda,
              también podemos ayudarte. ¡Escríbenos!
            </Trans>
          </p>
        </section>

        <div className="mt-8 flex justify-end">
          <button
            className="cursor-pointer border border-[rgba(170,83,63,0.25)] px-4 py-2 text-sm font-medium text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-soft)]"
            data-testid="account-signout-button"
            onClick={() => setShowLogoutConfirm(true)}
            type="button"
          >
            <Trans>Cerrar Sesión</Trans>
          </button>
        </div>
      </main>

      {showLogoutConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="mx-4 w-full max-w-sm border border-[var(--color-border)] bg-white p-6 shadow-[0_28px_70px_rgba(0,0,0,0.12)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2 font-display text-3xl leading-none text-[var(--color-ink)]">
              <Trans>Cerrar Sesión</Trans>
            </h2>
            <p className="mb-6 text-sm text-[var(--color-muted)]">
              <Trans>¿Estás seguro de que quieres cerrar sesión?</Trans>
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="app-btn-secondary px-4 py-2 text-sm"
                onClick={() => setShowLogoutConfirm(false)}
                type="button"
              >
                <Trans>Cancelar</Trans>
              </button>
              <button
                className="app-btn-primary px-4 py-2 text-sm"
                data-testid="account-signout-confirm"
                onClick={() => void handleSignOut()}
                type="button"
              >
                <Trans>Cerrar Sesión</Trans>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
